import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatMessage } from '../lib/db';
import { saveToGoogleDrive } from '../lib/googleDrive';
import AnnotatorModal from './AnnotatorModal';
import ThreeDViewer from './ThreeDViewer';
import { useSession } from '../store/session';
import { nanoid } from 'nanoid';
import { queueStudioImport } from '../lib/studioBridge';

// Renders message text, lifting fenced ``` code blocks into styled panels so
// code the director agent writes shows up readable in the chat (not raw text).
// Theme-safe: uses CSS variables only, so it inverts black/white with the app.
function MessageContent({ text }: { text: string }) {
  const parts = useMemo(() => {
    const segs: { type: 'text' | 'code'; label?: string; body: string }[] = [];
    const re = /```([^\n`]*)\n([\s\S]*?)```/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) segs.push({ type: 'text', body: text.slice(last, m.index) });
      segs.push({ type: 'code', label: (m[1] || '').trim() || 'code', body: m[2].replace(/\n$/, '') });
      last = re.lastIndex;
    }
    if (last < text.length) segs.push({ type: 'text', body: text.slice(last) });
    return segs;
  }, [text]);

  if (parts.length === 1 && parts[0].type === 'text') return <div>{text}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {parts.map((p, i) =>
        p.type === 'text'
          ? p.body.trim() && <div key={i} style={{ whiteSpace: 'pre-wrap' }}>{p.body}</div>
          : <CodePanel key={i} label={p.label!} body={p.body} />
      )}
    </div>
  );
}

function CodePanel({ label, body }: { label: string; body: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm, 8px)', overflow: 'hidden', background: 'var(--bg-card, var(--glass))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', borderBottom: '1px solid var(--line)', fontSize: '9px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-dim, var(--fg))' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <button
          className="btn"
          style={{ minHeight: '18px', fontSize: '9px', padding: '0 6px', borderColor: 'var(--line)', color: 'var(--fg)' }}
          onClick={() => { navigator.clipboard?.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre style={{ margin: 0, padding: '10px', overflow: 'auto', maxHeight: '320px', fontSize: '11px', lineHeight: 1.5, color: 'var(--fg)' }}>
        <code>{body}</code>
      </pre>
    </div>
  );
}

function SaveToDriveButton({ blob, filename }: { blob: Blob; filename?: string }) {
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  async function handleSave() {
    setStatus('saving');
    try {
      const res = await saveToGoogleDrive(blob, filename || `asset-${Date.now()}`);
      if (res.success) {
        setStatus('saved');
        setTimeout(() => setStatus('idle'), 2500);
      } else {
        setStatus('error');
        setErrMsg(res.error || 'Failed to save');
        setTimeout(() => setStatus('idle'), 4000);
      }
    } catch (err: any) {
      setStatus('error');
      setErrMsg(err?.message || 'Error occurred');
      setTimeout(() => setStatus('idle'), 4000);
    }
  }

  return (
    <button 
      className="btn" 
      onClick={handleSave} 
      style={{ 
        minHeight: '22px', 
        fontSize: '9px', 
        padding: '0 8px', 
        background: 'var(--glass)',
        borderColor: status === 'saved' ? 'var(--fg)' : 'var(--line)',
        color: 'var(--fg)',
        alignSelf: 'flex-start',
        borderWidth: '1px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}
      disabled={status === 'saving'}
      title="Save to Google Drive"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
      </svg>
      {status === 'idle' && 'Save to Drive'}
      {status === 'saving' && 'Saving...'}
      {status === 'saved' && 'Saved to Drive!'}
      {status === 'error' && (errMsg || 'Error')}
    </button>
  );
}

export default function MessageBubble({ message }: { message: ChatMessage }) {
  const cls = message.role === 'user' ? 'msg msg--user' : 'msg msg--assistant';
  const urls = useMemo(() => (message.attachments ?? []).map(a => ({
    a, url: a.source || (a.blob && a.blob.size > 0 ? URL.createObjectURL(a.blob) : '')
  })), [message.attachments]);

  const addStaged = useSession(s => s.addStaged);
  const navigate = useNavigate();
  const [annotatingItem, setAnnotatingItem] = useState<{ url: string; kind: 'image' | 'video' | '3d' } | null>(null);

  const handleSendAnnotation = (annotatedBlob: Blob, notes: string) => {
    // Stage the custom image
    addStaged({
      id: nanoid(),
      kind: 'image', // Always send annotated thing as an image
      mime: annotatedBlob.type,
      blob: annotatedBlob,
      name: `annotation-${nanoid().slice(0, 5)}.png`
    });
    // Send notes to composer
    if (notes.trim()) {
      window.dispatchEvent(new CustomEvent('protolive:insert-prompt', { detail: `[Annotation Notes]: ${notes}` }));
    } else {
      window.dispatchEvent(new CustomEvent('protolive:insert-prompt', { detail: '[Annotation Attached for precise refinement]' }));
    }
  };

  return (
    <div className={cls}>
      <MessageContent text={message.content} />

      {urls.length > 0 && (
        <div className="msg__attachments" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
          {urls.map(({ a, url }) => {
            if (a.kind === 'video') {
              return (
                <div key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <video src={url} controls playsInline style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', borderRadius: 'var(--radius)' }} />
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                    <SaveToDriveButton blob={a.blob} filename={a.name || `film-${a.id.slice(0, 5)}.mp4`} />
                    <button
                      className="btn btn--primary annotator-action-button"
                      style={{ minHeight: '22px', fontSize: '9px', padding: '0 8px', gap: '4px' }}
                      onClick={() => setAnnotatingItem({ url, kind: 'video' })}
                    >
                      <span>🖌️</span> Annotate
                    </button>
                    <button
                      className="btn"
                      style={{ minHeight: '22px', fontSize: '9px', padding: '0 8px', gap: '4px', borderColor: 'var(--line)' }}
                      title="Open in ProtoCut Studio and drop on the timeline"
                      onClick={() => {
                        queueStudioImport({ url, name: a.name, type: 'video', newTrack: true });
                        navigate('/studio');
                      }}
                    >
                      <span>🎬</span> Send to Studio Timeline
                    </button>
                  </div>
                </div>
              );
            }
            if (a.kind === 'audio') {
              return (
                <div key={a.id} style={{ width: '100%', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <audio src={url} controls style={{ width: '100%', maxWidth: '280px', borderRadius: 'var(--radius-sm)' }} />
                  <div className="msg__meta" style={{ marginTop: '2px', opacity: 0.6 }}>Generated Wave Audio (.wav)</div>
                  <SaveToDriveButton blob={a.blob} filename={a.name || `synth-${a.id.slice(0, 5)}.wav`} />
                </div>
              );
            }
            if (a.mime === 'model/gltf-binary' || a.name?.endsWith('.glb')) {
              const isDisabled = message.meta?.disabled === true;
              return (
                <div key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                  {isDisabled ? (
                    <div style={{ padding: '24px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', textAlign: 'center', fontSize: '12px', color: 'var(--fg-dim)' }}>
                      3D Environment paused.
                      <button className="btn btn--outline" style={{ display: 'block', margin: '8px auto 0', padding: '6px 12px', fontSize: '11px', background: 'var(--bg-surface)' }} onClick={() => useSession.getState().updateMessage(message.id, m => ({ ...m, meta: { ...m.meta, disabled: false } }))}>
                        Resume Environment
                      </button>
                    </div>
                  ) : (
                    <>
                      <ThreeDViewer
                        url={url}
                        onAnnotate={() => setAnnotatingItem({ url, kind: '3d' })}
                      />
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                        <SaveToDriveButton blob={a.blob} filename={a.name || `model-assets-${a.id.slice(0, 5)}.glb`} />
                        <button
                          className="btn"
                          style={{ minHeight: '22px', fontSize: '9px', padding: '0 8px', gap: '4px', borderColor: 'var(--line)' }}
                          title="Pause the 3D environment"
                          onClick={() => useSession.getState().updateMessage(message.id, m => ({ ...m, meta: { ...m.meta, disabled: true } }))}
                        >
                          ⏸️ Pause 3D
                        </button>
                        <button
                          className="btn"
                          style={{ minHeight: '22px', fontSize: '9px', padding: '0 8px', gap: '4px', borderColor: 'var(--line)' }}
                          title="Drop the current 3D world into the Studio timeline as a Z-orderable layer"
                          onClick={async () => {
                            const { queueStudioImport } = await import('../lib/studioBridge');
                            const { useCreatorStore } = await import('../studio/three/store/useCreatorStore');
                            const snap = useCreatorStore.getState();
                            queueStudioImport({
                              type: '3d-scene',
                              name: a.name ?? '3D scene',
                              duration: 6,
                              newTrack: true,
                              sceneData: {
                                assets: snap.objects.map((x) => ({
                                  id: x.id, url: (x as any).url || `proto://shape/${x.type}`, name: (x as any).name || x.type, tag: 'user-created',
                                  position: x.position, rotation: x.rotation, scale: x.scale,
                                })),
                                lights: [],
                                environment: { preset: 'studio', background: '#000000' },
                              },
                            });
                            navigate('/studio');
                          }}
                        >
                          <span>🎬</span> Send 3D to Studio
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            }
            return (
              <div key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <img src={url} alt={a.name ?? ''} style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', borderRadius: 'var(--radius)' }} />
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                  <SaveToDriveButton blob={a.blob} filename={a.name || `render-${a.id.slice(0, 5)}.png`} />
                  <button 
                    className="btn btn--primary annotator-action-button"
                    style={{ minHeight: '22px', fontSize: '9px', padding: '0 8px', gap: '4px' }}
                    onClick={() => setAnnotatingItem({ url, kind: 'image' })}
                  >
                    <span>🖌️</span> Annotate
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {message.meta && (
        <div className="msg__meta">
          {typeof message.meta.model === 'string' && <span>· {message.meta.model}</span>}
          {typeof message.meta.provider === 'string' && <span> · {message.meta.provider}</span>}
        </div>
      )}
      
      {annotatingItem && (
        <AnnotatorModal 
          mediaUrl={annotatingItem.url}
          kind={annotatingItem.kind}
          onClose={() => setAnnotatingItem(null)}
          onSend={handleSendAnnotation}
        />
      )}
    </div>
  );
}